import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import { CONTEXT_LIBRARY_KEY, type ContextDocument } from '../context-library/useContextLibrary';

/**
 * The documents attached to one assistant thread, plus mutations to
 * upload/link/paste new ones or pull an existing library doc in. Every
 * document created here also lands in the org's shared context library
 * (same underlying store as `useContextLibrary`), so every mutation
 * invalidates both query keys.
 *
 * Modeled on `useContextLibrary.ts` (react-query, not the flow planner's
 * plain fetch+useState hook) — same polling and error-handling conventions.
 */
export function useAssistantDocuments(conversationId: string | null) {
  const queryClient = useQueryClient();
  const queryKey = ['assistant-documents', conversationId] as const;

  const query = useQuery({
    queryKey,
    enabled: conversationId !== null,
    refetchInterval: (q) => {
      const data = q.state.data as ContextDocument[] | undefined;
      if (!data) return false;
      const extracting = data.some((d) => d.status === 'pending' || d.status === 'processing');
      return extracting ? 4_000 : false;
    },
    queryFn: async (): Promise<ContextDocument[]> => {
      const response = await fetch(`${API_URL}/conversations/${conversationId}/documents`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to load attached documents');
      }
      const json = (await response.json()) as { ok: true; data: ContextDocument[] };
      return json.data;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: CONTEXT_LIBRARY_KEY });
  };

  const upload = useMutation({
    mutationFn: async (files: File[]): Promise<ContextDocument[]> => {
      const form = new FormData();
      for (const file of files) form.append('files', file);
      const response = await fetch(`${API_URL}/conversations/${conversationId}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to upload document');
      }
      const json = (await response.json()) as { ok: true; data: ContextDocument[] };
      return json.data;
    },
    onSuccess: invalidate,
  });

  const addLink = useMutation({
    mutationFn: async (input: { url: string; description?: string }): Promise<ContextDocument> => {
      const response = await fetch(`${API_URL}/conversations/${conversationId}/documents/link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to add link');
      }
      const json = (await response.json()) as { ok: true; data: ContextDocument };
      return json.data;
    },
    onSuccess: invalidate,
  });

  const addPaste = useMutation({
    mutationFn: async (input: {
      content: string;
      label?: string;
      description?: string;
    }): Promise<ContextDocument> => {
      const response = await fetch(`${API_URL}/conversations/${conversationId}/documents/paste`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to save pasted text');
      }
      const json = (await response.json()) as { ok: true; data: ContextDocument };
      return json.data;
    },
    onSuccess: invalidate,
  });

  const attachFromLibrary = useMutation({
    mutationFn: async (documentId: string): Promise<ContextDocument> => {
      const response = await fetch(
        `${API_URL}/conversations/${conversationId}/documents/${documentId}/attach`,
        { method: 'POST', credentials: 'include' }
      );
      if (!response.ok) {
        throw await toApiError(response, 'Failed to attach document');
      }
      const json = (await response.json()) as { ok: true; data: ContextDocument };
      return json.data;
    },
    onSuccess: invalidate,
  });

  const detach = useMutation({
    mutationFn: async (documentId: string): Promise<void> => {
      const response = await fetch(
        `${API_URL}/conversations/${conversationId}/documents/${documentId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!response.ok) {
        throw await toApiError(response, 'Failed to remove document');
      }
    },
    onSuccess: invalidate,
  });

  return {
    // No `isLoading`: the chips render from `documents`, and an empty chip row
    // is the correct thing to show while the first fetch is in flight.
    documents: query.data ?? [],
    upload,
    addLink,
    addPaste,
    attachFromLibrary,
    detach,
  };
}
